/**
 * A service's registered templates, and whether each is ready to send with.
 *
 * "Ready" means every {#var#} slot says where its value comes from — the send
 * form, typed once, or each recipient's contact details — because Connect fills
 * the slots in order from those. A template that is not ready is listed, and says
 * what it is missing, rather than hidden.
 *
 * Each row's switch says whether the client sees the template at all. Off, it
 * is absent from every client surface — the web app, the phone and the API —
 * because the server leaves inactive templates out of everything a client can
 * read or send with; this switch only sets that flag.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  QueryPending,
  Select,
  Switch,
  api,
  toast,
} from '@connect/web-shared';

import { ChannelTemplateDialog } from './channel-template-dialog';
import { WhatsAppTemplateDialog } from './whatsapp-template-dialog';
import {
  messagingKeys,
  type ChannelDef,
  type ChannelTemplate,
  type ClientChannel,
  type ProviderDef,
  type TemplateSyncResult,
} from './types';

export function ChannelTemplatesCard({
  channel,
  def,
  provider,
  clientName,
}: {
  channel: ClientChannel;
  def: ChannelDef;
  provider: ProviderDef;
  clientName: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ChannelTemplate | 'new' | null>(null);
  const [removing, setRemoving] = useState<ChannelTemplate | null>(null);

  // One row's switch or type, saved on its own. The server checks the type
  // against the provider's list, so the selector offers exactly that list.
  const update = useMutation({
    mutationFn: ({
      template,
      changes,
    }: {
      template: ChannelTemplate;
      changes: { is_active?: boolean; category?: string };
    }) => api.patch<ChannelTemplate>(`/messaging/templates/${template.public_id}`, changes),
    onSuccess: (saved, { changes }) => {
      queryClient.setQueryData<ChannelTemplate[]>(
        messagingKeys.templates(channel.public_id),
        (rows) => rows?.map((row) => (row.public_id === saved.public_id ? saved : row)),
      );
      void queryClient.invalidateQueries({
        queryKey: messagingKeys.channels(channel.client_public_id),
      });
      toast.success(
        changes.category !== undefined
          ? `${saved.name} is now ${saved.category}`
          : saved.is_active
            ? `${saved.name} is shown to ${clientName}`
            : `${saved.name} is hidden from ${clientName}`,
      );
    },
    onError: (error) =>
      toast.error('Could not change it', error instanceof ApiError ? error.message : undefined),
  });
  const updating = (template: ChannelTemplate) =>
    update.isPending && update.variables?.template.public_id === template.public_id;

  const templates = useQuery({
    queryKey: messagingKeys.templates(channel.public_id),
    queryFn: () => api.get<ChannelTemplate[]>(`/messaging/channels/${channel.public_id}/templates`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: messagingKeys.templates(channel.public_id) });
    void queryClient.invalidateQueries({
      queryKey: messagingKeys.channels(channel.client_public_id),
    });
  };

  // Where the provider can list them, sync is the usual way in: the ids and
  // text arrive exactly as registered, and only the variables are left to set up.
  const canSync = channel.capabilities.includes('sync_templates');
  // Where the provider takes new templates (WhatsApp), they can be made here.
  const canCreate = channel.capabilities.includes('create_templates');
  const [making, setMaking] = useState(false);
  const [alsoAtProvider, setAlsoAtProvider] = useState(false);
  const sync = useMutation({
    mutationFn: () =>
      api.post<TemplateSyncResult>(`/messaging/channels/${channel.public_id}/templates/sync`),
    onSuccess: (result) => {
      queryClient.setQueryData(messagingKeys.templates(channel.public_id), result.templates);
      void queryClient.invalidateQueries({
        queryKey: messagingKeys.channels(channel.client_public_id),
      });
      toast.success(
        `Synced from ${provider.label}`,
        `${result.created} new, ${result.updated} changed` +
          (result.not_at_provider.length
            ? ` · not listed by the provider: ${result.not_at_provider.join(', ')}`
            : ''),
      );
    },
    onError: (error) =>
      toast.error('Could not sync', error instanceof ApiError ? error.message : undefined),
  });

  return (
    <Card>
      <CardHeader
        title={`${def.template_label}s`}
        actions={
          <Can permission="messaging.template.create">
            <div className="flex flex-wrap items-center gap-1.5">
              {canSync ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={sync.isPending}
                  onClick={() => sync.mutate()}
                >
                  <RefreshCw className="size-3.5" />
                  Sync from {provider.label}
                </Button>
              ) : null}
              {canCreate ? (
                <Button size="sm" onClick={() => setMaking(true)}>
                  <Plus className="size-3.5" />
                  New {provider.label.split(' ')[0]} template
                </Button>
              ) : null}
              {provider.manual_templates ? (
                <Button size="sm" onClick={() => setEditing('new')}>
                  <Plus className="size-3.5" />
                  Add template
                </Button>
              ) : null}
            </div>
          </Can>
        }
      />
      <CardBody>
        {templates.data === undefined ? (
          <QueryPending query={templates} label="Loading templates" />
        ) : !templates.data?.length ? (
          <p className="text-sm text-muted">
            No templates yet.{' '}
            {!provider.manual_templates
              ? `Create them at ${provider.label}, sync them here, then set up each one's variables.`
              : canSync
              ? `Sync them from ${provider.label}, then set up each one's variables.`
              : `Add each approved template from ${provider.label}, then set up its variables.`}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {templates.data.map((template) => {
              const attributes = provider.template_attributes
                .filter((spec) => template.attributes[spec.key])
                .map((spec) => `${spec.label} ${template.attributes[spec.key]}`);
              return (
                <li key={template.public_id} className="space-y-1.5 py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-heading">
                        {template.name}
                        {template.is_ready ? (
                          <Badge tone="success">Ready</Badge>
                        ) : (
                          <Badge tone="warning">{toSetUp(template)}</Badge>
                        )}
                        {template.is_active ? null : <Badge tone="neutral">Hidden from client</Badge>}
                        {provider.template_categories.length && !def.category_per_send ? (
                          // Fixed when the provider approved the template: shown, not chosen.
                          template.category ? <Badge tone="neutral">{template.category}</Badge> : null
                        ) : provider.template_categories.length ? (
                          <Can
                            permission="messaging.template.edit"
                            fallback={
                              template.category ? (
                                <Badge tone="neutral">{template.category}</Badge>
                              ) : null
                            }
                          >
                            <Select
                              aria-label={`SMS type of ${template.name}`}
                              size="sm"
                              className="w-auto"
                              options={provider.template_categories.map((value) => ({
                                value,
                                label: value,
                              }))}
                              value={template.category ?? ''}
                              disabled={updating(template)}
                              onChange={(event) =>
                                update.mutate({ template, changes: { category: event.target.value } })
                              }
                            />
                          </Can>
                        ) : null}
                        {template.encoding ? (
                          <Badge tone="neutral">
                            {provider.template_encodings.find((e) => e.value === template.encoding)
                              ?.label ?? template.encoding}
                          </Badge>
                        ) : null}
                      </p>
                      <p className="text-2xs text-faint">
                        {[
                          `${provider.label} template ${template.provider_template_id}`,
                          ...attributes,
                          ...(template.attributes.language ? [`Language ${template.attributes.language}`] : []),
                        ].join(' · ')}
                      </p>
                      {template.attributes.unsupported ? (
                        // Synced but not sendable from Connect yet; the provider's reason.
                        <p className="text-2xs text-warning">
                          Cannot be sent from Connect yet. {template.attributes.unsupported}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3">
                      <Can permission="messaging.template.edit">
                        <Switch
                          size="sm"
                          label="Shown to client"
                          checked={template.is_active}
                          disabled={updating(template)}
                          onChange={(event) =>
                            update.mutate({ template, changes: { is_active: event.target.checked } })
                          }
                        />
                      </Can>
                      <Can permission="messaging.template.edit">
                        <Button variant="secondary" size="sm" onClick={() => setEditing(template)}>
                          <Pencil className="size-3.5" />
                          {template.is_ready ? 'Edit' : 'Set up'}
                        </Button>
                      </Can>
                      <Can permission="messaging.template.delete">
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={`Remove ${template.name}`}
                          onClick={() => setRemoving(template)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </Can>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap rounded-control bg-surface-2 px-2.5 py-1.5 font-mono text-xs text-body">
                    {template.body}
                  </p>
                  {template.variable_count ? (
                    <p className="flex flex-wrap gap-1 text-2xs text-muted">
                      {template.variables.map((variable, index) => (
                        <Badge key={index} tone={variable ? 'info' : 'warning'}>
                          {index + 1}.{' '}
                          {variable
                            ? `${variable.label} · ${variable.source === 'form' ? 'form' : 'contact'}`
                            : 'not set up'}
                        </Badge>
                      ))}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>

      <ChannelTemplateDialog
        channel={channel}
        def={def}
        provider={provider}
        clientName={clientName}
        template={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={invalidate}
      />
      <WhatsAppTemplateDialog channel={channel} open={making} onClose={() => setMaking(false)} onMade={invalidate} />
      <Confirm
        open={removing !== null}
        onClose={() => {
          setRemoving(null);
          setAlsoAtProvider(false);
        }}
        title="Remove this template?"
        message={
          <div className="space-y-3">
            <p>
              {removing?.name} will no longer be available for this service's messages.
              {canCreate && !alsoAtProvider ? ` It stays registered at ${provider.label}, and a sync brings it back.` : ''}
            </p>
            {canCreate ? (
              <Switch
                label={`Also delete it at ${provider.label}`}
                checked={alsoAtProvider}
                onChange={(e) => setAlsoAtProvider(e.target.checked)}
              />
            ) : null}
          </div>
        }
        confirmLabel="Remove"
        onConfirm={async () => {
          if (!removing) return;
          try {
            await api.delete(`/messaging/templates/${removing.public_id}`, {
              params: alsoAtProvider ? { at_provider: true } : undefined,
            });
            invalidate();
          } catch (caught) {
            toast.error('Could not remove it', caught instanceof ApiError ? caught.message : undefined);
          }
        }}
      />
    </Card>
  );
}

/** What stands between a template and being ready to send with. */
export function toSetUp(template: ChannelTemplate): string {
  const unset = template.variables.filter((variable) => variable === null).length;
  const parts: string[] = [];
  if (template.header_needed && template.header?.format !== template.header_needed)
    parts.push(template.header_needed === 'location' ? 'place' : template.header_needed);
  if (unset) parts.push(`${unset} variable${unset === 1 ? '' : 's'}`);
  return `${parts.join(' and ')} to set up`;
}
